import React, { useCallback, useMemo } from 'react'
import { AutoComplete } from 'antd'
import { useSelector } from 'react-redux'
import {
  mapInvoiceToTableRow,
  resolveInvoiceNumberForAttach,
} from 'src/utils/invoiceUploadHelpers'

const { Option } = AutoComplete

function AttachInvoiceNumberInput({
  value = '',
  onChange,
  onSelectInvoice,
  defaultSupplierTitle,
  order,
  placeholder = 'Search or enter supplier invoice number',
}) {
  const uploadedInvoices = useSelector(
    state => state.newInvoices.uploadedInvoices,
  )

  const pendingInvoices = useMemo(
    () =>
      (uploadedInvoices || [])
        .filter(inv => inv.status === 'pending' || inv.status === undefined)
        .map(mapInvoiceToTableRow),
    [uploadedInvoices],
  )

  const buildOptions = useCallback(
    query => {
      const normalizedQuery = String(query || '')
        .trim()
        .toLowerCase()

      let filtered = pendingInvoices

      if (normalizedQuery) {
        filtered = pendingInvoices.filter(invoice =>
          [
            invoice.invoiceNumber,
            invoice.supplierTitle,
            invoice.purchaseOrderNumber,
            invoice.fileName,
            invoice.notes,
          ].some(field =>
            String(field || '')
              .toLowerCase()
              .includes(normalizedQuery),
          ),
        )
      }

      if (defaultSupplierTitle) {
        filtered = [...filtered].sort((left, right) => {
          const leftMatches = left.supplierTitle === defaultSupplierTitle
          const rightMatches = right.supplierTitle === defaultSupplierTitle

          if (leftMatches && !rightMatches) {
            return -1
          }

          if (!leftMatches && rightMatches) {
            return 1
          }

          return 0
        })
      }

      return filtered.slice(0, 15)
    },
    [defaultSupplierTitle, pendingInvoices],
  )

  const options = useMemo(() => buildOptions(value), [buildOptions, value])

  const handleSearch = useCallback(
    searchValue => {
      onChange(searchValue)
    },
    [onChange],
  )

  const handleSelect = useCallback(
    (selectedValue, option) => {
      const invoice = option?.props?.invoice

      if (invoice) {
        onSelectInvoice(invoice)

        const resolvedNumber = resolveInvoiceNumberForAttach(invoice, order)
        if (resolvedNumber) {
          onChange(resolvedNumber)
        }

        return
      }

      onChange(selectedValue)
    },
    [onChange, onSelectInvoice, order],
  )

  return (
    <AutoComplete
      allowClear
      style={{ width: '100%' }}
      value={value}
      placeholder={placeholder}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onFocus={() => handleSearch(value)}
      notFoundContent={
        value
          ? 'No matching invoices. You can still enter a number manually.'
          : 'Type to search uploaded invoices'
      }
    >
      {options.map(invoice => (
        <Option key={invoice.id} value={invoice.id} invoice={invoice}>
          {(invoice.invoiceNumber || 'No invoice number') +
            (invoice.supplierTitle ? ` — ${invoice.supplierTitle}` : '')}
          {invoice.fileName ? ` (${invoice.fileName})` : ''}
        </Option>
      ))}
    </AutoComplete>
  )
}

export default AttachInvoiceNumberInput
